import type { SemanticType } from '../../../semanticTypes'

/**
 * Renders a semantic type as a runtime variable path the switch task can
 * resolve at evaluation time — e.g. `'sequence.quote.currency'` →
 * `'$["sequence.quote.currency"]'`. Bracket access is used because the
 * semantic-type strings contain dots, which would otherwise look like nested
 * property access.
 */
export const semanticTypeRuntimePath = (semanticType: SemanticType): string =>
  `$[${JSON.stringify(semanticType)}]`

const SCOPE_DELIMITER = '::'

/**
 * Runtime key for a semantic type published by a condition's
 * `valueSupportingTask` rather than by an upstream node's `outputMapping`.
 *
 * The scope (e.g. a quote-template id) keeps two rows targeting different
 * subjects in separate keys, so neither can shadow the other when the
 * mappings are merged:
 *
 *   row 1 (tpl-a) -> sequence.quoteTemplate.attachments::tpl-a
 *   row 2 (tpl-b) -> sequence.quoteTemplate.attachments::tpl-b
 */
export const scopedSemanticTypeKey = ({
  semanticType,
  scope
}: {
  semanticType: SemanticType
  scope: string
}): string => `${semanticType}${SCOPE_DELIMITER}${scope}`

/**
 * Bracket-form runtime path for a scoped semantic type. The counterpart to
 * `scopedSemanticTypeKey` — function strings emit this, supporting tasks
 * publish the key.
 */
export const scopedSemanticTypeRuntimePath = (params: {
  semanticType: SemanticType
  scope: string
}): string => `$[${JSON.stringify(scopedSemanticTypeKey(params))}]`

/**
 * Takes the subject back off a key, undoing `scopedSemanticTypeKey`. A key that
 * was never scoped comes back untouched, so callers can run everything through
 * here without checking which kind they're holding.
 *
 *   sequence.quoteTemplate.attachments::tpl-a -> sequence.quoteTemplate.attachments
 *   sequence.quote.currency                   -> sequence.quote.currency
 */
export const stripSemanticTypeScope = (key: string): string =>
  key.split(SCOPE_DELIMITER).at(0) ?? key
